import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Info, ToggleLeft, ToggleRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_LABELS = {
  national_partner:  "National Partner",
  regional_partner:  "Regional Partner",
  zonal_partner:     "Zonal Partner",
  sales_manager:     "Sales Manager",
  sales_partner:     "Sales Partner",
  recognition_fund:  "Recognition Fund",
};

const LEVEL_ORDER = ["sales_partner", "sales_manager", "zonal_partner", "regional_partner", "national_partner", "recognition_fund"];
const LEVEL_COLORS = {
  sales_partner: "text-amber-600", sales_manager: "text-emerald-600",
  zonal_partner: "text-cyan-600", regional_partner: "text-blue-600",
  national_partner: "text-violet-600", recognition_fund: "text-rose-600",
};

function RateRow({ rate, onChange, saving }) {
  return (
    <tr className="border-b border-border/50 hover:bg-muted/20">
      <td className="px-4 py-3">
        <span className={`text-[12px] font-semibold ${LEVEL_COLORS[rate.partner_type]}`}>
          {LEVEL_LABELS[rate.partner_type] || rate.partner_type}
        </span>
      </td>
      <td className="px-4 py-3 text-muted-foreground text-[12px]">{rate.event_type === "subscription" ? "Subscription" : "Wallet Recharge"}</td>
      <td className="px-4 py-3 text-muted-foreground text-[12px]">Year {rate.year}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            type="number" value={rate.rate_pct} min={0} max={100} step={0.5}
            onChange={e => onChange(rate.id, parseFloat(e.target.value))}
            data-testid={`rate-input-${rate.id}`}
            className="w-20 px-2 py-1 bg-background border border-border rounded-lg text-[13px] text-right focus:outline-none focus:ring-1 focus:ring-primary/50" />
          <span className="text-muted-foreground text-[12px]">%</span>
        </div>
      </td>
    </tr>
  );
}

export default function SMCommissionConfig() {
  const [rates, setRates] = useState([]);
  const [globalCfg, setGlobalCfg] = useState({});
  const [changes, setChanges] = useState({});
  const [globalChanges, setGlobalChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/sm/commission-config`, { withCredentials: true }),
      axios.get(`${API}/api/sm/commission-config/global`, { withCredentials: true }),
    ]).then(([rRes, gRes]) => {
      const sorted = [...(rRes.data.rates || [])].sort((a, b) => {
        if (a.event_type !== b.event_type) return a.event_type.localeCompare(b.event_type);
        if (a.year !== b.year) return a.year - b.year;
        return LEVEL_ORDER.indexOf(a.partner_type) - LEVEL_ORDER.indexOf(b.partner_type);
      });
      setRates(sorted);
      setGlobalCfg(gRes.data.config || {});
    }).catch(() => toast.error("Failed to load commission config"))
    .finally(() => setLoading(false));
  }, []);

  const handleRateChange = (id, value) => {
    setRates(r => r.map(x => x.id === id ? { ...x, rate_pct: value } : x));
    setChanges(c => ({ ...c, [id]: value }));
  };

  const handleGlobalChange = (key, value) => {
    setGlobalCfg(g => ({ ...g, [key]: value }));
    setGlobalChanges(c => ({ ...c, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        ...Object.entries(changes).map(([id, rate_pct]) =>
          axios.patch(`${API}/api/sm/commission-config/${id}`, { rate_pct }, { withCredentials: true })
        ),
        ...Object.entries(globalChanges).map(([key, value]) =>
          axios.patch(`${API}/api/sm/commission-config/global`, { key, value }, { withCredentials: true })
        ),
      ]);
      toast.success("Commission configuration saved");
      setChanges({});
      setGlobalChanges({});
    } catch { toast.error("Failed to save changes"); }
    finally { setSaving(false); }
  };

  const subYear1 = rates.filter(r => r.event_type === "subscription" && r.year === 1);
  const subYear2 = rates.filter(r => r.event_type === "subscription" && r.year === 2);
  const wallet   = rates.filter(r => r.event_type === "wallet_recharge");
  const totalY1  = subYear1.filter(r => r.partner_type !== "recognition_fund").reduce((s, r) => s + r.rate_pct, 0);
  const totalY2  = subYear2.filter(r => r.partner_type !== "recognition_fund").reduce((s, r) => s + r.rate_pct, 0);

  const SectionTable = ({ rows, title, badge }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <div className="text-[13px] font-semibold text-foreground">{title}</div>
        {badge && <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border rounded-full px-2 py-0.5">{badge}</span>}
      </div>
      <table className="w-full text-[12px]">
        <thead className="border-b border-border bg-muted/20">
          <tr>
            {["Level", "Event", "Year", "Commission Rate"].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => <RateRow key={r.id} rate={r} onChange={handleRateChange} saving={saving} />)}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div className="p-8 text-muted-foreground animate-pulse">Loading configuration…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="sm-commission-config">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Commission Configuration</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Configure rates, GST, and TDS settings</p>
        </div>
        <button onClick={handleSave} disabled={saving || (Object.keys(changes).length === 0 && Object.keys(globalChanges).length === 0)}
          data-testid="save-config-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-40 transition-all">
          <Save className="w-4 h-4" />{saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* Global Settings */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="text-[13px] font-semibold text-foreground">Global Settings</div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">

          <div className="flex items-center justify-between">
            <div>
              <div className="text-[12px] font-medium">GST Enabled</div>
              <div className="text-[11px] text-muted-foreground">Commission calculated on net (after GST) when ON</div>
            </div>
            <button onClick={() => handleGlobalChange("gst_enabled", !globalCfg.gst_enabled)}
              data-testid="gst-toggle"
              className={`w-10 h-5 rounded-full transition-all ${globalCfg.gst_enabled ? "bg-primary" : "bg-muted border border-border"}`}>
              <span className={`block w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5 ${globalCfg.gst_enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>

          {globalCfg.gst_enabled && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">GST Rate (%)</label>
              <input type="number" value={globalCfg.gst_rate || 18} min={0} max={30}
                onChange={e => handleGlobalChange("gst_rate", parseFloat(e.target.value))}
                className="w-24 px-2 py-1.5 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Default TDS Rate (%)</label>
            <input type="number" value={globalCfg.default_tds || 10} min={0} max={30} step={0.5}
              onChange={e => handleGlobalChange("default_tds", parseFloat(e.target.value))}
              data-testid="default-tds-input"
              className="w-24 px-2 py-1.5 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Company TAN (for TDS Certificate)</label>
            <input value={globalCfg.company_tan || ""} onChange={e => handleGlobalChange("company_tan", e.target.value)}
              placeholder="AAAA00000A" className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Company PAN</label>
            <input value={globalCfg.company_pan || ""} onChange={e => handleGlobalChange("company_pan", e.target.value)}
              className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
          </div>
        </div>
      </div>

      {/* Commission Pool Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Year 1 Pool</div>
          <div className="text-2xl font-bold text-primary">{totalY1.toFixed(1)}%</div>
          <div className="text-[11px] text-muted-foreground">of net subscription value</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Year 2+ Pool</div>
          <div className="text-2xl font-bold text-foreground">{totalY2.toFixed(1)}%</div>
          <div className="text-[11px] text-muted-foreground">of net subscription value</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Status</div>
          <div className="text-[13px] font-medium text-foreground flex items-center gap-1.5 mt-1">
            <Info className="w-3.5 h-3.5 text-primary" />
            {Object.keys(changes).length > 0 || Object.keys(globalChanges).length > 0
              ? <span className="text-amber-600">Unsaved changes</span>
              : <span className="text-emerald-600">All saved</span>}
          </div>
        </div>
      </div>

      {/* Rate tables */}
      <SectionTable rows={subYear1} title="Subscription Commission — Year 1" badge="Preset defaults" />
      <SectionTable rows={subYear2} title="Subscription Commission — Year 2 Onwards" />
      <SectionTable rows={wallet}   title="Wallet Recharge Commission" badge="Set 0 for ineligible levels" />
    </div>
  );
}
